// external libs
import React from 'react'
import { Form, FormSpy } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import { Grid, makeStyles } from '@material-ui/core'

// hooks & stores
import useVerifyDocument from './useVerifyDocument'
import useManageProjectStore from '../../../../../../stores/manageProject/useManageProjectStore'
import useCheckPermission from '../../../../../../utilities/checkPermission'

// constants
import { VerifyDocumentViewType, VerifyDocumentType } from './verifyDocumentType'
import { ethicsFormResetList } from '../../../../shareUI/ethicsForm/formResetList'
import { researcherFormResetList } from '../../../../shareUI/researcherForm/formResetList'

// components
import ResearchDetail from '../../../../shareUI/ResearchDetail/researchDetail'
import DocumentTestResult from '../../../../shareUI/documentTestResult/documentTestResult'
import UploadDoc from '../../../../shareUI/uploadDoc/uploadDoc'
import PageAction from '../../pageAction'
import Dialog from '../../../../../../control/dialog/dialog'
import DisplayUpdater from '../../../../shareUI/displayUpdater/displayUpdater'
import EthicsForm from '../../../../shareUI/ethicsForm/ethicsForm'
import ModeAccordion from '../../../../shareUI/modeAccordion/ModeAccordion'
import ResearchForm from '../../../../shareUI/researcherForm/researchForm'
import ResearcherDetail from '../../../../shareUI/researcherDetail/researcherDetail'
import UploadAdditionalDoc from '../../../../shareUI/uploadAdditionalDoc/UploadAdditionalDoc'

const VerifyDocumentView = (props: VerifyDocumentViewType) => {
    const {
        formRef,
        isViewMode,
        projectData,
        configDialog,
        initialValues,
        isShowUpdateDate,
        initialUploadFile,
        projectDetailLabel,
        onRefDialog,
        onSubmitForm,
        onDirtyChange,
        checkUseUploadEdit,
        onClickBackToSearch,
        onChangeDocCompletely,
        confirmEditDocResult,
        handleEditDocResult,
        confirmEditEthics,
        researcherDetailLabel,
        confirmEditUploadDoc,
    } = props
    const manageProjectStore = useManageProjectStore()
    const { documentTopic } = manageProjectStore
    const checkPermission = useCheckPermission()
    const canOfficerEdit = checkPermission.isAllowOfficerToEdit(projectData)
    const isDisabledTestResult = canOfficerEdit ? false : true
    const canOfficerSaveDraft = checkPermission.checkSaveDraft()

    return (
        <Form
            onSubmit={onSubmitForm}
            initialValues={{ ...initialValues, uploadFile: initialUploadFile }}
            mutators={{
                ...arrayMutators,
            }}
            keepDirtyOnReinitialize={true}
        >
            {(props) => {
                formRef.current = props
                return (
                    <React.Fragment>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {checkPermission.isUserAdmin() ? (
                                    <ModeAccordion
                                        title={projectDetailLabel}
                                        renderEditComponent={() => (
                                            <EthicsForm
                                                hideAccordion={true}
                                                headerLabel={projectDetailLabel}
                                                isDisableRIB={true}
                                            />
                                        )}
                                        renderViewComponent={() => (
                                            <ResearchDetail hideAccordion={true} dataSource={projectData} />
                                        )}
                                        fieldNames={ethicsFormResetList}
                                        isViewMode={true}
                                        onConfirm={() => confirmEditEthics(props, ethicsFormResetList)}
                                    />
                                ) : (
                                    <ResearchDetail dataSource={projectData} />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                {checkPermission.isUserAdmin() ? (
                                    <ModeAccordion
                                        title={researcherDetailLabel}
                                        renderEditComponent={() => (
                                            <ResearchForm
                                                hideAccordion={true}
                                                headerLabel={projectDetailLabel}
                                                isDisableRIB={true}
                                            />
                                        )}
                                        renderViewComponent={() => (
                                            <ResearcherDetail hideAccordion={true} dataSource={projectData} />
                                        )}
                                        fieldNames={researcherFormResetList}
                                        isViewMode={true}
                                        onConfirm={() => confirmEditEthics(props, researcherFormResetList)}
                                    />
                                ) : (
                                    <ResearcherDetail dataSource={projectData} />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <UploadDoc
                                    initialData={initialUploadFile}
                                    documentTitleList={documentTopic}
                                    latestDocument={projectData?.latestDocument}
                                    isViewMode={true}
                                    canEditUploadFile={checkPermission.isUserAdmin()}
                                    handleConfirm={async () => {
                                        await confirmEditUploadDoc(props, ['uploadFile'])
                                    }}
                                    isUseUploadfileEdit={checkUseUploadEdit()}
                                    zipDownload={true}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <UploadAdditionalDoc isViewMode={true} isEditable={checkPermission.isUserAdmin()} />
                            </Grid>

                            <Grid item xs={12}>
                                <DocumentTestResult
                                    initialData={projectData}
                                    onChangeDocCompletely={onChangeDocCompletely}
                                    isViewMode={isViewMode}
                                    useAccordionMode={!isDisabledTestResult && isViewMode}
                                    onSubmitEdit={async () => {
                                        await confirmEditDocResult(props, [
                                            'isCompletelyDoc',
                                            'resubmitDocDate',
                                            'remark',
                                            'remarkWithFormat',
                                        ])
                                    }}
                                    onClickModeEdit={() => handleEditDocResult(props)}
                                />
                            </Grid>

                            {isShowUpdateDate && <DisplayUpdater />}

                            {!isViewMode && (
                                <Grid item xs={12}>
                                    <PageAction
                                        onClickSaveDraft={
                                            canOfficerSaveDraft
                                                ? () => {
                                                      props.form.change('submitType', 'draft')
                                                      props.handleSubmit()
                                                  }
                                                : undefined
                                        }
                                        onClickSave={() => {
                                            props.form.change('submitType', 'save')
                                            props.handleSubmit()
                                        }}
                                        onClickClose={onClickBackToSearch}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <FormSpy subscription={{ dirty: true }} onChange={onDirtyChange} />
                        <Dialog onRef={onRefDialog} {...configDialog} />
                    </React.Fragment>
                )
            }}
        </Form>
    )
}

//Controller
const VerifyDocument = (props: VerifyDocumentType) => {
    const verifyDocumentModel = useVerifyDocument(props)
    return <VerifyDocumentView {...verifyDocumentModel} {...props} />
}

export default VerifyDocument
